/**
 * DS Compare — Competitor image resolution (v5.4, reinstated)
 * ---------------------------------------------------------------
 * Thumbnails are back. Lessons from the v5.x attempts are baked in:
 * bot-blocking is server-side (the Worker gets 403'd by Amazon, ulo.co,
 * regrowthlabs and friends), but the SHOPPER'S BROWSER is an ordinary
 * visitor and loads those images fine. So every tier resolves in the
 * browser, and every URL is verified by actually loading it — a dead
 * URL can never leave a broken-image icon.
 *
 * Resolution order (first verified source wins):
 *   1. `direct`   — curated image URL from the competitor_product
 *                   metaobject (`image_url` field). Admin-controlled,
 *                   most reliable; set this for dropdown products.
 *   2. `workerUrl`— whatever image URL the Cloudflare Worker scraped
 *                   from the product page / photo analysis.
 *   3. `dataUrl`  — the shopper's own uploaded photo (photo flow).
 *                   Always renders; it's already in memory.
 *   4. microlink.io on `pageUrl` — CORS-enabled service that renders
 *                   the page (JS and all) and returns its og:image.
 *   5. Placeholder: "Product image is unavailable." — a designed
 *                   fallback so the frame is never an empty hole.
 *
 * API:
 *   window.DSCMP_IMAGE({ direct, workerUrl, dataUrl, pageUrl, name, brand })
 *   window.DSCMP_IMAGE_RESET()
 */
(function () {
  var MICROLINK = 'https://api.microlink.io/?url=';
  var LOOKUP_MS = 9000;
  var VERIFY_MS = 8000;
  var seq = 0; // guards against out-of-order async results when the shopper switches products quickly

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function frame() { return document.getElementById('dscmp-cimg'); }

  function unavailableHtml() {
    return '<span class="dscmp-imgframe__unavail">Product image is unavailable.</span>';
  }

  function loadingHtml() {
    return '<span class="dscmp-spinner" style="width:26px;height:26px;border-width:2.5px;"></span>';
  }

  /* Verify a URL by actually loading it. Resolves '' on failure/timeout. */
  function verify(url) {
    return new Promise(function (resolve) {
      if (!url) { resolve(''); return; }
      if (/^data:image\//i.test(url)) { resolve(url); return; }
      if (!/^https:\/\//i.test(url)) { resolve(''); return; }
      var img = new Image();
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(''); } }, VERIFY_MS);
      img.onload = function () {
        if (done) return;
        done = true; clearTimeout(t);
        resolve(img.naturalWidth > 60 && img.naturalHeight > 60 ? url : '');
      };
      img.onerror = function () {
        if (done) return;
        done = true; clearTimeout(t); resolve('');
      };
      img.src = url;
    });
  }

  function fromMicrolink(pageUrl) {
    return new Promise(function (resolve) {
      if (!pageUrl || !/^https?:\/\//i.test(pageUrl)) { resolve(''); return; }
      var done = false;
      var t = setTimeout(function () { if (!done) { done = true; resolve(''); } }, LOOKUP_MS);
      fetch(MICROLINK + encodeURIComponent(pageUrl))
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) {
          if (done) return;
          done = true; clearTimeout(t);
          var d = j && j.data;
          var img = (d && d.image && d.image.url) || (d && d.logo && d.logo.url) || '';
          resolve(typeof img === 'string' ? img : '');
        })
        .catch(function () {
          if (done) return;
          done = true; clearTimeout(t); resolve('');
        });
    });
  }

  function show(el, url, name) {
    el.classList.remove('is-loading');
    el.innerHTML = '<img src="' + esc(url) + '" alt="' + esc(name || 'Product image') + '" loading="lazy">';
  }

  function fallback(el) {
    el.classList.remove('is-loading');
    el.innerHTML = unavailableHtml();
  }

  window.DSCMP_IMAGE_RESET = function () {
    seq++;
    var el = frame();
    if (!el) return;
    fallback(el);
  };

  window.DSCMP_IMAGE = function (opts) {
    opts = opts || {};
    var el = frame();
    if (!el) return;

    var my = ++seq;
    el.classList.add('is-loading');
    el.innerHTML = loadingHtml();

    function alive() { return my === seq && frame(); }

    verify(String(opts.direct || '').trim()).then(function (ok) {
      if (!alive()) return;
      if (ok) { show(el, ok, opts.name); return; }
      return verify(String(opts.workerUrl || '').trim()).then(function (ok2) {
        if (!alive()) return;
        if (ok2) { show(el, ok2, opts.name); return; }
        var dataUrl = String(opts.dataUrl || '').trim();
        if (/^data:image\//i.test(dataUrl)) { show(el, dataUrl, opts.name); return; }
        return fromMicrolink(String(opts.pageUrl || '').trim()).then(function (found) {
          if (!alive()) return;
          return verify(found).then(function (ok3) {
            if (!alive()) return;
            if (ok3) show(el, ok3, opts.name);
            else fallback(el);
          });
        });
      });
    })['catch'](function () {
      if (alive()) fallback(el);
    });
  };
})();
